/**
 * Day/night solar terminator layer.
 * Renders a semi-transparent dark polygon over the nighttime side of Earth.
 * Updates every 5 minutes to track the sun's position.
 *
 * Uses precise astronomical formulas: Julian date, solar declination,
 * right ascension, GMST to compute the subsolar point, then traces the
 * terminator latitude at 1° longitude intervals.
 *
 * The night polygon is constructed as a ring: terminator curve +
 * closure through the dark pole, producing a single simple polygon
 * that deck.gl PolygonLayer renders as a filled region.
 */

import { PolygonLayer } from '@deck.gl/layers';

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

/**
 * Compute the night polygon — the hemisphere of Earth facing away from the sun.
 * Returns an array of [lng, lat] tuples forming a closed polygon ring.
 *
 * Construction:
 * 1. Trace the terminator curve (the circle where solar elevation = 0)
 *    at 1° longitude steps from -180 to +180.
 * 2. Close the polygon by sweeping along the dark pole edge
 *    (+90 for northern winter, -90 for southern winter).
 */
function computeNightPolygon(): [number, number][] {
  const now = new Date();
  const JD = now.getTime() / 86400000 + 2440587.5; // Julian Date
  const D = JD - 2451545.0; // Days since J2000.0

  // Solar mean anomaly (radians)
  const g = (((357.529 + 0.98560028 * D) % 360 + 360) % 360) * DEG;

  // Solar ecliptic longitude (degrees, then radians)
  const q = ((280.459 + 0.98564736 * D) % 360 + 360) % 360;
  const L = q + 1.915 * Math.sin(g) + 0.020 * Math.sin(2 * g);
  const LRad = L * DEG;

  // Obliquity of ecliptic (radians)
  const eRad = (23.439 - 0.00000036 * D) * DEG;

  // Solar declination (radians)
  const decl = Math.asin(Math.sin(eRad) * Math.sin(LRad));

  // Solar right ascension (radians)
  const RA = Math.atan2(Math.cos(eRad) * Math.sin(LRad), Math.cos(LRad));

  // Greenwich Mean Sidereal Time (degrees)
  const GMST = (((18.697374558 + 24.06570982441908 * D) % 24 + 24) % 24) * 15;

  // Sub-solar longitude (degrees, normalized to [-180, 180])
  let sunLng = RA * RAD - GMST;
  sunLng = ((sunLng % 360) + 540) % 360 - 180;

  const tanDecl = Math.tan(decl);
  const points: [number, number][] = [];

  // Determine which pole is in darkness
  // decl > 0 → northern summer → south pole is dark
  // decl < 0 → southern summer → north pole is dark
  // decl ≈ 0 → equinox → split evenly
  const darkPoleLat = decl > 0 ? -90 : 90;

  // Near-equinox: terminator is nearly a vertical great circle
  if (Math.abs(tanDecl) < 1e-6) {
    // Night side is 90° to 270° from the sub-solar point
    const nightCenter = ((sunLng + 180) % 360 + 360) % 360 - 180;
    for (let lat = -90; lat <= 90; lat += 1) {
      points.push([nightCenter + 90, lat]);
    }
    for (let lat = 90; lat >= -90; lat -= 1) {
      points.push([nightCenter - 90, lat]);
    }
    return points;
  }

  // General case: trace terminator at 1° longitude steps
  for (let lng = -180; lng <= 180; lng += 1) {
    const ha = (lng - sunLng) * DEG; // Hour angle
    const lat = Math.atan(-Math.cos(ha) / tanDecl) * RAD;
    points.push([lng, lat]);
  }

  // Close polygon through the dark pole:
  // 1. From last terminator point [180, lat] → [180, darkPoleLat]
  // 2. Sweep along the pole edge back to [-180, darkPoleLat]
  // 3. deck.gl auto-closes back to first point [-180, lat_at_-180]
  points.push([180, darkPoleLat]);
  points.push([-180, darkPoleLat]);

  return points;
}

// --- Cached polygon + layer creation ---

let cachedPolygon: [number, number][] | null = null;

/** Create the deck.gl terminator layer. */
export function createTerminatorLayer(): PolygonLayer {
  if (!cachedPolygon) {
    cachedPolygon = computeNightPolygon();
  }

  return new PolygonLayer({
    id: 'day-night-layer',
    data: [{ polygon: cachedPolygon }],
    getPolygon: (d: { polygon: [number, number][] }) => d.polygon,
    getFillColor: [0, 0, 20, 55],
    filled: true,
    stroked: true,
    getLineColor: [200, 200, 255, 25],
    getLineWidth: 1,
    lineWidthUnits: 'pixels' as const,
    pickable: false,
  });
}

// --- Auto-update timer (every 5 minutes) ---

let updateInterval: ReturnType<typeof setInterval> | null = null;
let onUpdateCallback: (() => void) | null = null;

/** Start the terminator auto-update timer. */
export function startTerminatorUpdates(onUpdate: () => void): void {
  stopTerminatorUpdates();
  onUpdateCallback = onUpdate;
  cachedPolygon = computeNightPolygon();
  updateInterval = setInterval(() => {
    cachedPolygon = computeNightPolygon();
    onUpdateCallback?.();
  }, 5 * 60 * 1000); // Every 5 minutes
}

/** Stop the terminator auto-update timer. */
export function stopTerminatorUpdates(): void {
  if (updateInterval !== null) {
    clearInterval(updateInterval);
    updateInterval = null;
  }
  onUpdateCallback = null;
  cachedPolygon = null;
}
